import { tns } from "tiny-slider";
import { store } from "jiffy-store";
import { track } from "jiffy-analytics";

import idleCallback from "common/utils/ui/idle_callback";
import { applyModalNavigation } from "common/jiffy/accessibility";
import { productImageCarousel } from "components/jiffy/product-details-image-carousel/product-details-image-carousel";

import "tiny-slider/src/tiny-slider.scss";
import "./product-lightbox.scss";

const SELECTORS = {
  PRODUCT_LIGHTBOX: ".product-lightbox",
  PRODUCT_LIGHTBOX_IMAGE: ".product-lightbox__image",
  PRODUCT_LIGHTBOX_BACK_BUTTON: ".product-lightbox__back-button",
  PRODUCT_LIGHTBOX_IMAGE_CONTAINER: ".product-lightbox__image-container",
  CAROUSEL_WRAPPER_SELECTOR: ".js-product-lightbox-wrapper",
  PDP_CAROUSEL: ".js-carousel-wrapper:not(.hidden)",
  PDP_CAROUSEL_ITEM: ".js-carousel-item",
  PDP_CAROUSEL_THUMBNAILS: ".js-carousel-thumbnails",
  PDP_CAROUSEL_IMAGE: ".js-carousel-image",
  PRODUCT_LIGHTBOX_CAROUSEL: ".product-lightbox__carousel-items",
  PRODUCT_LIGHTBOX_THUMBNAILS: ".js-product-lightbox-thumbnails",
  PRODUCT_LIGHTBOX_HEADER_COUNTER: ".product-lightbox__header-counter"
};

const getProductLightbox$ = () => document.querySelector(SELECTORS.PRODUCT_LIGHTBOX);
const getProductLightboxImage$ = () => document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_IMAGE);
const getBackButton$ = () => document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_BACK_BUTTON);
const getImageContainer$ = () => document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_IMAGE_CONTAINER);
let productLightBoxCarousel = null;

const onProductLightboxCarouselIndexChange = (index, indexDiff = 0) => {
  const counterElement = document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_HEADER_COUNTER);
  if (counterElement) {
    counterElement.textContent = `${index + 1} of ${productLightBoxCarousel.getInfo().slideCount}`;
  }

  const thumbnails = document.querySelectorAll(".product-lightbox__carousel-thumbnail-image");
  thumbnails.forEach((thumbnail, idx) => {
    if (idx === index) {
      thumbnail.classList.add("active");
    } else {
      thumbnail.classList.remove("active");
    }
  });

  productImageCarousel?.selectItemByIndex(index + indexDiff);
};

const createThumbnailImage = (src, index) => {
  const image = document.createElement("img");
  image.className = "product-lightbox__carousel-thumbnail-image";
  image.src = src;
  image.addEventListener("click", () => {
    productLightBoxCarousel?.goTo(index);
  });

  return image;
};

const createCarouselImage = src => {
  const div = document.createElement("div");
  div.className = "product-lightbox__carousel-item product-lightbox__image-container";
  const image = document.createElement("img");
  image.className = "product-lightbox__image";
  image.src = src;
  image.loading = "lazy";
  div.appendChild(image);

  return div;
};

const setupCarousel = (carouselContainer, indexDiff = 0) => {
  const startIndex = productImageCarousel.currentItem || 0 - indexDiff;
  productLightBoxCarousel = tns({
    container: carouselContainer,
    controls: false,
    nav: false,
    loop: false,
    startIndex
  });
  productLightBoxCarousel.events.on("indexChanged", info => {
    const index = info.displayIndex - 1;
    onProductLightboxCarouselIndexChange(index, indexDiff);
  });
  productLightBoxCarousel.goTo(startIndex);
  onProductLightboxCarouselIndexChange(startIndex, indexDiff);
};

const setupLightboxSlider = () => {
  const carouselContainer = document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_CAROUSEL);
  const thumbnailsContainer = document.querySelector(SELECTORS.PRODUCT_LIGHTBOX_THUMBNAILS);
  const pdpCarousel = document.querySelector(SELECTORS.PDP_CAROUSEL);
  if (!carouselContainer || !thumbnailsContainer || !pdpCarousel) return;

  carouselContainer.textContent = "";
  thumbnailsContainer.textContent = "";

  const items = Array.from(pdpCarousel.querySelectorAll(SELECTORS.PDP_CAROUSEL_ITEM));
  idleCallback(() => {
    let indexDiff = 0;
    items.forEach((element, idx) => {
      const image = element.querySelector(SELECTORS.PDP_CAROUSEL_IMAGE);
      const imageSrc = image?.parentElement?.dataset.imgSrc || image?.dataset.src;
      const thumbnailSrc = image?.dataset.thumbnailSrc;
      if (imageSrc && thumbnailSrc) {
        thumbnailsContainer.appendChild(createThumbnailImage(thumbnailSrc, idx));
        carouselContainer.appendChild(createCarouselImage(imageSrc));
      } else {
        indexDiff += 1;
      }
    });
    setupCarousel(carouselContainer, indexDiff);
  });
};

const openLightbox = (showTransparentBg = false) => {
  const productLightbox = getProductLightbox$();
  store.ui.change({ lightboxOpened: true });
  productLightbox?.classList.remove("hidden");
  productLightbox?.setAttribute("tabindex", "0");
  document.documentElement.classList.add("locked-all");
  setupLightboxSlider();
  if (showTransparentBg) {
    getImageContainer$()?.classList.add("product-lightbox__transparent-container");
  }
  idleCallback(() => getBackButton$()?.focus());
};

const closeLightbox = () => {
  const productLightbox = getProductLightbox$();
  store.ui.change({ lightboxOpened: false });
  productLightbox.classList.add("hidden");
  productLightbox?.setAttribute("tabindex", "-1");
  getImageContainer$().classList.remove("product-lightbox__transparent-container");
  document.documentElement.classList.remove("locked-all");
  track("product_image_preview_close");
  idleCallback(() => {
    productLightBoxCarousel?.destroy();
    productLightBoxCarousel = null;
  });
};

export default (
  carouselWrapperSelector = SELECTORS.CAROUSEL_WRAPPER_SELECTOR,
  showTransparentBg = false
) => {
  const carouselWrapper = document.querySelector(carouselWrapperSelector);
  if (!carouselWrapper) return;

  const productLightbox = getProductLightbox$();
  if (!productLightbox) return;

  const productLightboxImage = getProductLightboxImage$();
  const backButton = getBackButton$();

  carouselWrapper.addEventListener(
    "click",
    // eslint-disable-next-line func-names
    function (e) {
      track("product_image_preview_open", {});
      // loop parent nodes from the target to the delegation node
      for (let { target } = e; target && target !== this; target = target.parentNode) {
        if (target.matches(".js-product-lightbox-link")) {
          openLightbox(showTransparentBg);
          productLightboxImage?.removeAttribute("src");
          productLightboxImage?.setAttribute("src", target.dataset.imgSrc);
          break;
        }
      }
    },
    false
  );

  backButton?.addEventListener("click", closeLightbox);

  productLightbox.addEventListener("keydown", e => {
    if (e.key === "Enter" || e.key === "Escape") {
      closeLightbox();
    }
  });

  productLightbox.addEventListener("click", () => {
    idleCallback(() => productLightbox.focus());
  });

  applyModalNavigation(productLightbox);
};
